import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import { BlogpayLoginModal } from "@app/components/modals/PlatformModal/Blogpay";
import { Cafe24LoginModal } from "@app/components/modals/PlatformModal/Cafe24";
import { CAFE24_SCOPES } from "@app/pages/auth/platform/cafe24/Login";
import {Steps} from "@app/pages/auth/Signup/type"
import { ADMIN_ACCOUNT, SIGNATURE, CAFE24_CLIENT_ID } from "@app/utils/keys";
import { Button, Input, Label, LoadingButton, useModal } from "@shortsworks/ui";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, KeyboardEvent, useState } from "react";
import {
  useSignIn,
  useGetAccountPlatform,
  getAccountPlatform,
  getProductListForAuthCheck,
  clearAuthToken,
  ErrorCode,
} from "@shortsworks/user-api";
import FormContainer from "src/components/forms/container/FormContainer";
import { useFormWithSchema } from "@app/components/forms/hooks/useFormWithSchema";
import {
  LoginSchema,
  AdminLoginSchema,
} from "@app/pages/auth/Login/loginSchema";
import { LoaderCircle } from "lucide-react";
import { setAuthToken } from "@shortsworks/user-api";
import { Path } from "@app/route_path";
import { PlatformType } from "@app/types/platform";

// admin login 등 추가
export function Login() {
  const { showModal } = useModal();
  const navigate = useNavigate();
  const destination = Path.DASHBOARD;

  const [isAdmin, setIsAdmin] = useState(false);
  const {
    mutate: mutateSignIn,
    data: signInData,
    isPending: isPendingSignIn,
    isError: isErrorSignIn,
    isSuccess: isSuccessSignIn,
  } = useSignIn();

  const { form, schemas, parseForm } = useFormWithSchema({
    formSchemas: LoginSchema,
    mode: "onBlur",
  });

  const adminLoginForm = useFormWithSchema({
    formSchemas: AdminLoginSchema,
    mode: "onBlur",
  });

  const handleClickSignup = () => {
    return navigate(Path.SIGNUP, {
      state: {
        [Steps.PATH]: Steps.STEP1
      }
    });
  }

  useEffect(() => {
    form.setFocus("email");

    // document.addEventListener("keypress", handlePressLogin);
    // return () => docuemnt.removeEventListener("keypress", handlePressLogin);
  }, []);

  const handleLogin = () => {
    const parsedForm = parseForm();
    if (parsedForm.success) {
      const { email, password } = parsedForm.data;

      return mutateSignIn({
        data: {
          email,
          password,
          destination,
        },
      });
    }
  };

  const handleLoginByAdmin = async () => {
    const parsedAdminLoginForm = adminLoginForm.parseForm();
    if (!parsedAdminLoginForm.success) {
      return;
    }
    const { email } = parsedAdminLoginForm.data;
    const loginByAdminResponse = await fetch(
      `https://api.shortsdev.com/auth/sign-in/simple/email?email=${encodeURIComponent(email)}&signature=${SIGNATURE}`,
      {
        headers: {
          "x-api-version": "2024-10-16",
        },
      },
    );
    const loginByAdminResult = await loginByAdminResponse.json();

    if (loginByAdminResult.error) {
      if (loginByAdminResult.error.code === ErrorCode.NUMBER_1003) {
        return showModal(CommonErrorModal, {
          title: "로그인 실패",
          description: "해당 계정이 존재하지 않습니다.",
        });
      }
      return showModal(CommonErrorModal, { title: "로그인에 실패했습니다." });
    }

    const { accessToken, refreshToken } = loginByAdminResult.data;

    setAuthToken({
      accessToken,
      refreshToken,
    });

    return navigate(destination);
  };

  useEffect(() => {
    if (!isSuccessSignIn) {
      return;
    }
    (async () => {
      const parsedForm = parseForm();
      if (parsedForm.success && parsedForm.data.email === ADMIN_ACCOUNT) {
        return setIsAdmin(true);
      }

      setAuthToken({
        // @ts-ignore
        accessToken: signInData.data.accessToken,
        // @ts-ignore
        refreshToken: signInData.data.refreshToken,
      });
      // @ts-ignore
      const redirectUrl = signInData.data.redirectUrl;

      // 그 외 플랫폼을 가져올 필요 없음
      const accountPlatform = await getAccountPlatform();
      if (accountPlatform.error) {
        clearAuthToken();
        return showModal(CommonErrorModal, {
          title: "회원 정보를 불러오는데 실패했습니다.",
        });
      }

      const { platformType, platformId, apiKey, shopNo } = accountPlatform.data;

      if (platformType === PlatformType.BLOGPAY) {
        const getProductListForAuthCheckResult =
          await getProductListForAuthCheck({
            platformType,
            platformId,
            apiKey,
            shopNo: shopNo.toString(),
          });

        if (getProductListForAuthCheckResult.error) {
          const blogpayLoginModalResult =
            await showModal(BlogpayLoginModal).promise();

          if (!blogpayLoginModalResult.value) {
            clearAuthToken();
            return showModal(CommonErrorModal, {
              title: "로그인에 실패했습니다.",
            });
          }
        }
      }

      if (platformType === PlatformType.CAFE24) {
        // CAFE24 로그인이 발생하는 경우
        if (redirectUrl !== destination) {
          // ENV 파일로 관리 필요
          const redirectState = btoa(
            `${platformId}@${import.meta.env.VITE_FONTEND_URL}${Path.LOGIN_CAFE24}?mall_id=${platformId}`,
          );
          const cafe24AuthRedirectURL = `https://${platformId}.cafe24.com/api/v2/oauth/authorize?response_type=code&client_id=${CAFE24_CLIENT_ID}&redirect_uri=${"https://api.shortsdev.com"}/external/auth/&state=${redirectState}&scope=${CAFE24_SCOPES}`;
          const cafe24LoginModalResult: { value: boolean } = showModal(
            Cafe24LoginModal,
            { redirectUrl: cafe24AuthRedirectURL },
          ).promise();
          // redirectURL 를 미리 기재하기엔 cafe24 유저임을 알 수 없고 mallId를 알 수 없음

          if (!cafe24LoginModalResult.value) {
            // 취소할 경우, 로그인된 토큰 정보 삭제
            return clearAuthToken();
          }
          return;
        }
      }

      return navigate(redirectUrl);
    })();
  }, [isPendingSignIn, isErrorSignIn, isSuccessSignIn]);

  return (
    <div className="flex items-center justify-center py-12 max-wfull px-4">
      <div className="mx-auto grid w-[350px] gap-6">
        <div className="grid gap-2">
          <h1 className="text-3xl font-bold">로그인</h1>
          {isErrorSignIn && (
            <p className="text-balance text-destructive">
              로그인이 실패하였습니다.
              <br />
              이메일과 비밀번호를 확인해주세요.
            </p>
          )}
        </div>
        {
          !isAdmin && (
          <form onSubmit={(event) => {
            event.preventDefault();
            return handleLogin();
          }}>
            <div className="grid gap-4">
              <FormContainer schemas={schemas} form={form} />
              <div className="flex items-center justify-end">
                <Link
                  to={Path.FIND_PASSWORD}
                  className="ml-auto inline-block text-sm underline"
                >
                  비밀번호를 잊으셨나요?
                </Link>
              </div>
              <LoadingButton
                className="w-full"
                onClick={handleLogin}
                loading={isPendingSignIn}
              >
                로그인
              </LoadingButton>
            </div>
            <div className="mt-4 text-center text-sm">
              아직 회원이 아니신가요?{" "}
              <div onClick={handleClickSignup} className="cursor-pointer underline">
                회원가입
              </div>
            </div>
          </form>
        )}
        {isAdmin && (
          <>
            <FormContainer
              schemas={adminLoginForm.schemas}
              form={adminLoginForm.form}
            />
            <Button onClick={handleLoginByAdmin}>로그인</Button>
          </>
        )}
      </div>
    </div>
  );
}
